import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { todoService } from './services/todoService';
import SyncSettings from './components/SyncSettings';

function App() {
  const [todos, setTodos] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [draggedItem, setDraggedItem] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('darkMode');
    return savedTheme ? JSON.parse(savedTheme) : true;
  });
  const [showCongrats, setShowCongrats] = useState(false);
  const [previousTodos, setPreviousTodos] = useState([]);
  const [showUndo, setShowUndo] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState('');
  const [showSyncStatus, setShowSyncStatus] = useState(false);
  const [priorityDividerIndex, setPriorityDividerIndex] = useState(0);
  
  // Reference to the timeout for hiding the sync status
  const syncStatusTimeoutRef = useRef(null);
  // Reference for touch events
  const touchStartYRef = useRef(null);
  const touchItemIndexRef = useRef(null);
  const touchingRef = useRef(false);

  // Function to fetch todos from the API
  const fetchTodos = async () => {
    setIsLoading(true);
    setSyncStatus('Syncing...');
    setShowSyncStatus(true);
    
    try {
      const { todos: fetchedTodos, priorityDividerIndex: fetchedDividerIndex } = await todoService.getTodos();
      setTodos(fetchedTodos);
      
      // Set the priority divider index if it exists
      if (fetchedDividerIndex !== undefined) {
        setPriorityDividerIndex(fetchedDividerIndex);
      }
      
      setSyncStatus('Synced');
      
      // Hide the sync status after 3 seconds if it's "Synced"
      if (syncStatusTimeoutRef.current) {
        clearTimeout(syncStatusTimeoutRef.current);
      }
      syncStatusTimeoutRef.current = setTimeout(() => {
        setShowSyncStatus(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to load todos:', error);
      setSyncStatus('Offline');
      setShowSyncStatus(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Load todos from API on component mount
  useEffect(() => {
    fetchTodos();
    
    // Set up polling for updates every 30 seconds
    const intervalId = setInterval(fetchTodos, 30000);
    
    return () => {
      clearInterval(intervalId);
      if (syncStatusTimeoutRef.current) {
        clearTimeout(syncStatusTimeoutRef.current);
      }
    };
  }, []);

  // Save todos to API whenever they change
  useEffect(() => {
    if (todos.length > 0 || !isLoading) {
      const saveTodos = async () => {
        setSyncStatus('Syncing...');
        setShowSyncStatus(true);
        
        // Save the priority divider index along with todos
        const success = await todoService.saveTodos(todos, priorityDividerIndex);
        
        setSyncStatus(success ? 'Synced' : 'Offline');
        
        // Hide the sync status after 3 seconds if it's "Synced"
        if (success) {
          if (syncStatusTimeoutRef.current) {
            clearTimeout(syncStatusTimeoutRef.current);
          }
          syncStatusTimeoutRef.current = setTimeout(() => {
            setShowSyncStatus(false);
          }, 3000);
        }
      };
      
      saveTodos();
    }
  }, [todos, isLoading, priorityDividerIndex]);

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    document.body.className = darkMode ? 'dark-mode' : 'light-mode';
  }, [darkMode]);

  useEffect(() => {
    if (todos.length > 0 && todos.every(todo => todo.completed)) {
      setShowCongrats(true);
    } else {
      setShowCongrats(false);
    }
  }, [todos]);

  const toggleTheme = () => {
    setDarkMode(!darkMode);
  };

  // Handle manual sync when sync settings change
  const handleSync = () => {
    fetchTodos();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim() === '') return;
    
    const newTodo = {
      id: Date.now().toString(),
      text: inputValue,
      completed: false,
    };
    
    // Add the new todo below the priority divider
    const newTodos = [...todos];
    newTodos.splice(priorityDividerIndex, 0, newTodo);
    
    // No need to adjust the priority divider index since we're adding below it
    
    setTodos(newTodos);
    setInputValue('');
  };

  const toggleTodo = (id) => {
    setTodos(
      todos.map((todo) =>
        todo.id === id ? { ...todo, completed: !todo.completed } : todo
      )
    );
  };

  const deleteTodo = (id) => {
    const index = todos.findIndex(todo => todo.id === id);
    const newTodos = todos.filter(todo => todo.id !== id);
    
    // If the deleted task was above the divider, decrement the divider index
    if (index < priorityDividerIndex) {
      setPriorityDividerIndex(prev => Math.max(0, prev - 1));
    }
    
    setTodos(newTodos);
  };

  const clearAllTodos = () => {
    // Save the current todos for potential undo
    setPreviousTodos(todos);
    setShowUndo(true);
    
    // Clear all todos and reset the priority divider
    setTodos([]);
    setPriorityDividerIndex(0);
    
    // Hide the undo button after 10 seconds
    setTimeout(() => {
      setShowUndo(false);
    }, 10000);
  };

  const undoClearAll = () => {
    // Restore the previous todos and priority divider index
    setTodos(previousTodos);
    
    // Calculate an appropriate priority divider index based on the previous todos
    const savedPriorityDividerIndex = localStorage.getItem('priorityDividerIndex');
    if (savedPriorityDividerIndex) {
      const parsedIndex = parseInt(savedPriorityDividerIndex, 10);
      setPriorityDividerIndex(Math.min(parsedIndex, previousTodos.length));
    }
    
    setShowUndo(false);
  };

  const startEditing = (todo) => {
    setEditingId(todo.id);
    setEditValue(todo.text);
  };

  const handleEdit = (id) => {
    if (editValue.trim() !== '') {
      setTodos(
        todos.map((todo) =>
          todo.id === id ? { ...todo, text: editValue.trim() } : todo
        )
      );
      setEditingId(null);
      setEditValue('');
    }
  };

  const handleKeyPress = (e, id) => {
    if (e.key === 'Enter') {
      handleEdit(id);
    } else if (e.key === 'Escape') {
      setEditingId(null);
      setEditValue('');
    }
  };

  // Function to move a task above the priority divider
  const moveTaskAboveDivider = (id) => {
    const taskIndex = todos.findIndex(todo => todo.id === id);
    if (taskIndex === -1 || taskIndex < priorityDividerIndex) return;
    
    // Make a copy of the todos array
    const newTodos = [...todos];
    
    // Remove the task from its current position
    const [task] = newTodos.splice(taskIndex, 1);
    
    // Insert it at position 0 (top of the list)
    newTodos.splice(0, 0, task);
    
    // Increment the priority divider index since we added a task above it
    setPriorityDividerIndex(prev => prev + 1);
    
    // Update the todos state
    setTodos(newTodos);
  };
  
  // Function to move a task below the priority divider
  const moveTaskBelowDivider = (id) => {
    const taskIndex = todos.findIndex(todo => todo.id === id);
    if (taskIndex === -1 || taskIndex >= priorityDividerIndex) return;
    
    // Make a copy of the todos array
    const newTodos = [...todos];
    
    // Remove the task from its current position
    const [task] = newTodos.splice(taskIndex, 1);
    
    // Insert it at the priority divider position
    newTodos.splice(priorityDividerIndex - 1, 0, task);
    
    // Decrement the priority divider index since we removed a task from above it
    setPriorityDividerIndex(prev => prev - 1);
    
    // Update the todos state
    setTodos(newTodos);
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.target.closest('.todo-item').classList.add('dragging');
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    const itemElement = e.target.closest('.todo-item');
    
    // Remove drag-over class from all items
    document.querySelectorAll('.todo-item').forEach(item => {
      item.classList.remove('drag-over');
    });

    // Add drag-over class to the current target
    if (itemElement && draggedItem !== index) {
      itemElement.classList.add('drag-over');
    }
  };

  const handleDragLeave = (e) => {
    const itemElement = e.target.closest('.todo-item');
    if (itemElement) {
      itemElement.classList.remove('drag-over');
    }
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    
    // Clean up any drag-over styling
    document.querySelectorAll('.todo-item').forEach(item => {
      item.classList.remove('drag-over');
    });
    
    // If we're not dragging anything, exit
    if (draggedItem === null) return;
    
    // Make a copy of the todos array
    const newTodos = [...todos];
    
    // Remove the dragged item from its original position
    const [draggedTodo] = newTodos.splice(draggedItem, 1);
    
    // Insert it at the new position
    newTodos.splice(index, 0, draggedTodo);
    
    // Calculate the new priority divider index
    let newPriorityDividerIndex = priorityDividerIndex;
    
    // Adjust the divider index based on the movement
    if (draggedItem >= priorityDividerIndex && index < priorityDividerIndex) {
      // Moving from below to above the divider
      newPriorityDividerIndex++;
    } else if (draggedItem < priorityDividerIndex && index >= priorityDividerIndex) {
      // Moving from above to below the divider
      newPriorityDividerIndex--;
    }
    
    // Update the todos state and priority divider index
    setTodos(newTodos);
    setPriorityDividerIndex(newPriorityDividerIndex);
    
    // Reset the dragged item
    setDraggedItem(null);
  };

  const handleDragEnd = (e) => {
    e.target.closest('.todo-item').classList.remove('dragging');
    setDraggedItem(null);
  };

  // Touch event handlers for mobile drag and drop
  const handleTouchStart = (e, index) => {
    touchStartYRef.current = e.touches[0].clientY;
    touchItemIndexRef.current = index;
    
    setTimeout(() => {
      if (touchingRef.current) {
        setDraggedItem(index);
        e.target.closest('.todo-item').classList.add('dragging');
      }
    }, 200);
    
    touchingRef.current = true;
  };

  const handleTouchMove = (e, index) => {
    if (!touchingRef.current) return;
    
    if (draggedItem !== null) {
      e.preventDefault();
      
      const currentY = e.touches[0].clientY;
      const items = document.querySelectorAll('.todo-item');
      
      document.querySelectorAll('.todo-item').forEach(item => {
        item.classList.remove('drag-over');
      });
      
      items.forEach((item, idx) => {
        const rect = item.getBoundingClientRect();
        if (currentY >= rect.top && currentY <= rect.bottom && idx !== draggedItem) {
          item.classList.add('drag-over');
        }
      });
    }
  };

  const handleTouchEnd = (e) => {
    if (!touchingRef.current || draggedItem === null) {
      touchingRef.current = false;
      return;
    }
    
    touchingRef.current = false;
    
    const touchY = e.changedTouches[0].clientY;
    const items = document.querySelectorAll('.todo-item');
    
    let closestIndex = -1;
    let closestDistance = Infinity;
    
    items.forEach((item, idx) => {
      const rect = item.getBoundingClientRect();
      const itemMiddle = rect.top + rect.height / 2;
      const distance = Math.abs(touchY - itemMiddle);
      
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = idx;
      }
    });
    
    if (closestIndex !== -1 && closestIndex !== draggedItem) {
      handleDrop(e, closestIndex);
    }
    
    document.querySelectorAll('.todo-item').forEach(item => {
      item.classList.remove('dragging');
      item.classList.remove('drag-over');
    });
    
    setDraggedItem(null);
    touchItemIndexRef.current = null;
  };

  const handleTouchCancel = () => {
    touchingRef.current = false;
    if (draggedItem !== null) {
      document.querySelectorAll('.todo-item').forEach(item => {
        item.classList.remove('dragging');
        item.classList.remove('drag-over');
      });
      setDraggedItem(null);
    }
  };

  return (
    <div className={`App ${darkMode ? 'dark-theme' : 'light-theme'}`}>
      <div className="container">
        <div className="header-container">
          <h1>To-Do-List</h1>
          <div className="header-controls">
            <button 
              onClick={toggleTheme} 
              className="theme-toggle"
              title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {darkMode ? '☀️' : '🌙'}
            </button>
          </div>
        </div>
        
        {/* Sync Settings Button - Moved below header */}
        <div className="sync-settings-container">
          <SyncSettings onSync={handleSync} />
        </div>
        
        {showCongrats && (
          <div className="congrats-message">
            <h2>🎉 Congratulations! 🎉</h2>
            <p>You've completed all your tasks!</p>
          </div>
        )}
        
        {showUndo && (
          <div className="undo-container">
            <button onClick={undoClearAll} className="undo-button">
              Undo Clear All
            </button>
          </div>
        )}
        
        {/* Sync status toast notification */}
        {showSyncStatus && (
          <div 
            className={`sync-status-toast ${syncStatus === 'Synced' ? 'synced' : syncStatus === 'Syncing...' ? 'syncing' : 'offline'}`}
            onClick={() => {
              if (syncStatus === 'Synced') {
                setShowSyncStatus(false);
                if (syncStatusTimeoutRef.current) {
                  clearTimeout(syncStatusTimeoutRef.current);
                }
              }
            }}
          >
            <div className="sync-status-icon">
              {syncStatus === 'Synced' ? '✓' : syncStatus === 'Syncing...' ? '↻' : '!'}
            </div>
            <div className="sync-status-text">{syncStatus}</div>
          </div>
        )}
        
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading your tasks...</p>
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="todo-form">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Add a new to-do..."
                className="todo-input"
              />
              <button type="submit" className="add-button">
                Add
              </button>
            </form>
            
            {/* Mobile reordering hint */}
            <div className="mobile-hint">
              <p>Tip: Hold and drag ⋮⋮ to reorder tasks. Use Priority/Non-Priority buttons to organize important tasks.</p>
            </div>
            
            <div className="todo-list">
              {todos.length > 0 ? (
                todos.map((todo, index) => (
                  <React.Fragment key={todo.id}>
                    {index === priorityDividerIndex && (
                      <div className="priority-divider">
                        <div className="divider-line"></div>
                      </div>
                    )}
                    
                    <div
                      className="todo-item"
                      draggable="true"
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                      onTouchStart={(e) => handleTouchStart(e, index)}
                      onTouchMove={(e) => handleTouchMove(e, index)}
                      onTouchEnd={handleTouchEnd}
                      onTouchCancel={handleTouchCancel}
                    >
                      <div className="drag-handle" title="Hold and drag to reorder">⋮⋮</div>
                      <div className="todo-content">
                        <div className="todo-main">
                          <input
                            type="checkbox"
                            checked={todo.completed}
                            onChange={() => toggleTodo(todo.id)}
                            className="todo-checkbox"
                          />
                          {editingId === todo.id ? (
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={() => handleEdit(todo.id)}
                              onKeyDown={(e) => handleKeyPress(e, todo.id)}
                              className="edit-input"
                              autoFocus
                            />
                          ) : (
                            <span
                              onClick={() => !todo.completed && startEditing(todo)}
                              style={{
                                textDecoration: todo.completed ? 'line-through' : 'none',
                                cursor: todo.completed ? 'default' : 'pointer',
                              }}
                              className="todo-text"
                              title={todo.completed ? '' : 'Click to edit'}
                            >
                              {todo.text}
                            </span>
                          )}
                        </div>
                        <div className="todo-actions">
                          {index >= priorityDividerIndex ? (
                            <button
                              className="action-button priority-button high-priority"
                              onClick={() => moveTaskAboveDivider(todo.id)}
                              title="Move to top priority"
                            >
                              Non-Priority or Thoughts
                            </button>
                          ) : (
                            <button
                              className="action-button priority-button low-priority"
                              onClick={() => moveTaskBelowDivider(todo.id)}
                              title="Move to lower priority"
                            >
                              Priority
                            </button>
                          )}
                          <button
                            className="action-button delete-button"
                            onClick={() => deleteTodo(todo.id)}
                            title="Delete task"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                ))
              ) : (
                <div className="empty-todo-list">
                  <p>No tasks yet. Add a task to get started!</p>
                  <div className="priority-divider empty-list-divider">
                    <div className="divider-line"></div>
                  </div>
                  <p className="divider-hint">Tasks added will appear below the line. Use Priority button to mark tasks as important.</p>
                </div>
              )}
              
              {/* If the divider should be at the end of the list, render it there */}
              {todos.length > 0 && priorityDividerIndex === todos.length && (
                <div 
                  className="priority-divider"
                  draggable
                  title="Drag to move divider"
                >
                  <div className="divider-line"></div>
                </div>
              )}
            </div>
            {todos.length > 0 && (
              <div className="clear-all-container">
                <button onClick={clearAllTodos} className="clear-all-button">
                  Clear All
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default App;
